import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schemaOrg = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Política de Privacidade - Feenex",
  "description": "Política de Privacidade, Cookies e Termos de Uso da Feenex em conformidade com a LGPD",
  "url": `${SITE_URL}/politica-de-privacidade`,
  "inLanguage": "pt-BR",
  "isPartOf": {
    "@type": "WebSite",
    "name": "Feenex",
    "url": SITE_URL
  },
  "dateModified": new Date().toISOString().split('T')[0]
};

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const existingScripts = document.querySelectorAll('script[data-schema="privacy-policy"]');
    existingScripts.forEach((s) => s.remove());

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'privacy-policy');
    script.textContent = JSON.stringify(schemaOrg);
    document.head.appendChild(script);

    return () => {
      document.querySelectorAll('script[data-schema="privacy-policy"]').forEach((s) => s.remove());
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <main className="bg-feenex-white">
        {/* Header */}
        <div className="bg-feenex-blue text-white py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              Política de Privacidade, Cookies e Termos de Uso
            </h1>
            <p className="text-feenex-white/80 text-lg">
              Última atualização: {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Content */}
        <article className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className="prose prose-lg max-w-none">
            {/* Introdução */}
            <section className="mb-12">
              <h4 className="text-2xl font-bold text-feenex-blue mb-4">
                <a href="#introducao" id="introducao" className="hover:text-feenex-gold transition-colors">
                  1. Introdução
                </a>
              </h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                A <strong>FEENEX</strong> preza pela transparência, segurança e conformidade legal no tratamento de dados pessoais. Esta Política de Privacidade, Cookies e Termos de Uso descreve como coletamos, utilizamos, armazenamos e protegemos as informações de usuários e clientes, em conformidade com a <strong>Lei Geral de Proteção de Dados (Lei nº 13.709/2018 – LGPD)</strong> e demais normas aplicáveis.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Ao utilizar nosso site ou fornecer seus dados à FEENEX, você declara estar ciente e de acordo com as disposições aqui descritas.
              </p>
            </section>

            {/* Política de Privacidade */}
            <div className="bg-feenex-gold/10 border-l-4 border-feenex-gold px-6 py-4 mb-12">
              <h2 className="text-3xl font-bold text-feenex-blue mb-2">
                POLÍTICA DE PRIVACIDADE
              </h2>
            </div>

            {/* Dados Coletados */}
            <section className="mb-12">
              <h4 className="text-2xl font-bold text-feenex-blue mb-4">
                <a href="#dados-coletados" id="dados-coletados" className="hover:text-feenex-gold transition-colors">
                  2. Dados Coletados
                </a>
              </h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                A FEENEX poderá coletar dados pessoais e empresariais necessários à prestação de seus serviços, incluindo:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
                <li>Nome completo</li>
                <li>CPF ou CNPJ</li>
                <li>Cargo e empresa</li>
                <li>Endereço de e-mail</li>
                <li>Número de telefone</li>
                <li>Informações empresariais e tributárias fornecidas voluntariamente</li>
                <li>Dados necessários à análise de viabilidade de operações com direitos creditórios</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                A coleta ocorre exclusivamente quando necessária para contato, análise técnica, formalização contratual ou cumprimento de obrigações legais.
              </p>
            </section>

            {/* Finalidade do Tratamento */}
            <section className="mb-12">
              <h4 className="text-2xl font-bold text-feenex-blue mb-4">
                <a href="#finalidade" id="finalidade" className="hover:text-feenex-gold transition-colors">
                  3. Finalidade do Tratamento
                </a>
              </h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                Os dados pessoais são tratados exclusivamente para:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
                <li>Avaliar a adequação da empresa às soluções de direitos creditórios oferecidas</li>
                <li>Oferecer a solução mais adequada ao perfil do cliente</li>
                <li>Realizar contato comercial ou técnico</li>
                <li>Formalizar contratos e documentos</li>
                <li>Cumprir obrigações legais e regulatórias</li>
                <li>Garantir suporte e acompanhamento operacional</li>
              </ul>
              <div className="bg-feenex-blue/5 border border-feenex-blue/20 rounded-lg p-6 mb-4">
                <p className="text-gray-700 leading-relaxed font-semibold">
                  A FEENEX não comercializa, aluga ou compartilha dados pessoais para fins estranhos às suas atividades.
                </p>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Os dados de contato são utilizados exclusivamente para comunicação relacionada aos serviços da FEENEX e sempre em favor do cliente.
              </p>
            </section>

            {/* Base Legal */}
            <section className="mb-12">
              <h4 className="text-2xl font-bold text-feenex-blue mb-4">
                <a href="#base-legal" id="base-legal" className="hover:text-feenex-gold transition-colors">
                  4. Base Legal para Tratamento
                </a>
              </h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                O tratamento de dados pessoais é realizado com fundamento nas seguintes bases legais previstas na LGPD:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Consentimento do titular</li>
                <li>Execução de contrato ou procedimentos preliminares</li>
                <li>Cumprimento de obrigação legal ou regulatória</li>
                <li>Exercício regular de direitos em processo judicial, administrativo ou arbitral</li>
                <li>Legítimo interesse, quando aplicável e proporcional</li>
              </ul>
            </section>

            {/* Compartilhamento */}
            <section className="mb-12">
              <h4 className="text-2xl font-bold text-feenex-blue mb-4">
                <a href="#compartilhamento" id="compartilhamento" className="hover:text-feenex-gold transition-colors">
                  5. Compartilhamento de Dados
                </a>
              </h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                O compartilhamento de dados poderá ocorrer exclusivamente quando necessário para:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
                <li>Parceiros jurídicos e contábeis envolvidos na operação</li>
                <li>Fornecedores de tecnologia e hospedagem</li>
                <li>Autoridades públicas, mediante exigência legal</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Todos os terceiros contratados estão sujeitos a obrigações de confidencialidade e segurança compatíveis com a LGPD.
              </p>
            </section>

            {/* Segurança */}
            <section className="mb-12">
              <h4 className="text-2xl font-bold text-feenex-blue mb-4">
                <a href="#seguranca" id="seguranca" className="hover:text-feenex-gold transition-colors">
                  6. Segurança da Informação
                </a>
              </h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                A FEENEX adota medidas técnicas e administrativas adequadas para proteger os dados pessoais contra:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
                <li>Acessos não autorizados</li>
                <li>Vazamentos</li>
                <li>Alterações indevidas</li>
                <li>Destruição ou perda acidental</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                Entre as medidas aplicadas estão:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
                <li>Controle restrito de acesso</li>
                <li>Proteção de sistemas e servidores</li>
                <li>Monitoramento de segurança</li>
                <li>Políticas internas de confidencialidade</li>
              </ul>
              <div className="bg-feenex-blue/5 border border-feenex-blue/20 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed font-semibold">
                  As informações são utilizadas exclusivamente para viabilizar a melhor solução tributária possível ao cliente, garantindo confidencialidade, integridade e segurança.
                </p>
              </div>
            </section>

            {/* Retenção */}
            <section className="mb-12">
              <h4 className="text-2xl font-bold text-feenex-blue mb-4">
                <a href="#retencao" id="retencao" className="hover:text-feenex-gold transition-colors">
                  7. Retenção dos Dados
                </a>
              </h4>
              <p className="text-gray-700 leading-relaxed">
                Os dados pessoais serão armazenados apenas pelo tempo necessário ao cumprimento das finalidades para as quais foram coletados, observando obrigações legais, regulatórias e contratuais.
              </p>
            </section>

            {/* Direitos do Titular */}
            <section className="mb-12">
              <h4 className="text-2xl font-bold text-feenex-blue mb-4">
                <a href="#direitos" id="direitos" className="hover:text-feenex-gold transition-colors">
                  8. Direitos do Titular
                </a>
              </h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                Nos termos da LGPD, o titular poderá solicitar:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
                <li>Confirmação da existência de tratamento</li>
                <li>Acesso aos dados</li>
                <li>Correção de dados incompletos ou desatualizados</li>
                <li>Anonimização ou exclusão, quando aplicável</li>
                <li>Portabilidade</li>
                <li>Revogação do consentimento</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                As solicitações poderão ser feitas por meio do canal indicado na seção de contato.
              </p>
            </section>

            {/* Contato */}
            <section className="mb-12">
              <h4 className="text-2xl font-bold text-feenex-blue mb-4">
                <a href="#contato-lgpd" id="contato-lgpd" className="hover:text-feenex-gold transition-colors">
                  9. Contato e Encarregado de Dados
                </a>
              </h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                Para exercer seus direitos, esclarecer dúvidas ou fazer solicitações relacionadas ao tratamento de dados pessoais, entre em contato:
              </p>
              <div className="bg-feenex-blue/5 rounded-lg p-6 space-y-3">
                <div className="flex items-center gap-3">
                  <i className="ri-mail-line text-xl text-feenex-blue"></i>
                  <div>
                    <strong className="text-feenex-blue">E-mail:</strong>{' '}
                    <a href="mailto:comercial@feenex.com.br" className="text-feenex-gold hover:underline cursor-pointer">
                      comercial@feenex.com.br
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-xl text-feenex-blue"></i>
                  <div>
                    <strong className="text-feenex-blue">Telefone:</strong>{' '}
                    <a href="tel:+5511947705303" className="text-feenex-gold hover:underline cursor-pointer">
                      +55 (11) 94770-5303
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-xl text-feenex-blue mt-0.5"></i>
                  <div>
                    <strong className="text-feenex-blue">Endereço:</strong>{' '}
                    <span className="text-gray-700">Rua Natalino Poppi, 232 - Osasco, SP, 06030-170</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Alterações */}
            <section className="mb-12">
              <h4 className="text-2xl font-bold text-feenex-blue mb-4">
                <a href="#alteracoes" id="alteracoes" className="hover:text-feenex-gold transition-colors">
                  10. Alterações nesta Política
                </a>
              </h4>
              <p className="text-gray-700 leading-relaxed">
                A FEENEX reserva-se o direito de atualizar esta Política de Privacidade sempre que necessário, para refletir mudanças em suas práticas, legislação aplicável ou melhorias nos serviços. Recomendamos a leitura periódica deste documento.
              </p>
            </section>

            {/* Aceitação */}
            <section className="mb-12">
              <h4 className="text-2xl font-bold text-feenex-blue mb-4">
                <a href="#aceitacao" id="aceitacao" className="hover:text-feenex-gold transition-colors">
                  11. Aceitação dos Termos
                </a>
              </h4>
              <p className="text-gray-700 leading-relaxed">
                Ao utilizar o site da FEENEX ou fornecer seus dados pessoais, você declara ter lido, compreendido e concordado com os termos desta Política de Privacidade, Cookies e Termos de Uso.
              </p>
            </section>

            {/* Footer da Política */}
            <div className="border-t border-gray-200 pt-8 mt-12">
              <p className="text-sm text-gray-600 text-center">
                <strong>FEENEX</strong> – Soluções estruturadas em direitos creditórios para redução legal de impostos federais.
              </p>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}